import type { GridApi, GridGroupNode } from '@mui/x-data-grid-pro';
import { GRID_ROOT_GROUP_ID, gridRowNodeSelector } from '@mui/x-data-grid-pro';
import * as React from 'react';
import type { ExpansionState } from './tableExpansionStorage';
import {
  pruneExpansionEntries,
  readExpansionEntry,
  writeExpansionEntry,
} from './tableExpansionStorage';

export const PERSIST_DEBOUNCE_MS = 100;

const emptyExpansionState = (): ExpansionState => ({ type: 'include', ids: new Set() });

const setGroupExpansion = (
  apiRef: React.RefObject<GridApi>,
  id: number,
  expanded: boolean,
): void => {
  const node = gridRowNodeSelector(apiRef, id);
  if (!node || node.type !== 'group') return;
  if ((node as GridGroupNode).childrenExpanded === expanded) return;
  apiRef.current?.setRowChildrenExpansion(id, expanded);
};

const collectGroupIds = (apiRef: React.RefObject<GridApi>): Set<number> => {
  const api = apiRef.current;
  if (!api) return new Set();
  const ids = new Set<number>();
  for (const id of api.getAllRowIds()) {
    if (gridRowNodeSelector(apiRef, id)?.type === 'group') ids.add(Number(id));
  }
  return ids;
};

const collectExpansionState = (
  apiRef: React.RefObject<GridApi>,
  mode: 'include' | 'exclude',
): ExpansionState => {
  const api = apiRef.current;
  if (!api) return { type: mode, ids: new Set() };
  const ids = new Set<number>();
  for (const id of api.getAllRowIds()) {
    const node = gridRowNodeSelector(apiRef, id);
    if (node?.type !== 'group') continue;
    const isExpanded = (node as GridGroupNode).childrenExpanded;
    if (mode === 'include' ? isExpanded : !isExpanded) ids.add(Number(id));
  }
  return { type: mode, ids };
};

export const useTableExpansionStorage = ({
  instanceKey,
  apiRef,
  enabled,
}: {
  instanceKey: string | undefined;
  apiRef: React.RefObject<GridApi>;
  enabled: boolean;
}): { markExpanded: (id: number) => void } => {
  const initialExpansionState = React.useMemo(
    () => (instanceKey ? readExpansionEntry(instanceKey) : null),
    [instanceKey],
  );

  // The live expansion intent, kept in sync with user toggles. Reconciliation
  // reads from here rather than the stale mount-time initialExpansionState.
  const liveStateRef = React.useRef<ExpansionState>(initialExpansionState ?? emptyExpansionState());
  const modeRef = React.useRef<'include' | 'exclude'>(initialExpansionState?.type ?? 'include');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRestoringRef = React.useRef(false);
  const hasInitializedRef = React.useRef(false);
  const appliedGroupIdsRef = React.useRef<Set<number>>(new Set());

  // Reset the live state and reconciliation bookkeeping whenever the table
  // instance changes. Keyed on instanceKey identity (not initialExpansionState)
  // so it still fires when switching between two instances that both have no
  // saved state — initialExpansionState would be null for both.
  // biome-ignore lint/correctness/useExhaustiveDependencies: initialExpansionState is derived from instanceKey
  React.useEffect(() => {
    liveStateRef.current = initialExpansionState ?? emptyExpansionState();
    modeRef.current = initialExpansionState?.type ?? 'include';
    hasInitializedRef.current = false;
    appliedGroupIdsRef.current = new Set();
  }, [instanceKey]);

  React.useEffect(() => {
    if (!instanceKey) return;
    pruneExpansionEntries();
  }, [instanceKey]);

  const markExpanded = React.useCallback(
    (id: number) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        liveStateRef.current = collectExpansionState(apiRef, modeRef.current);
      }
      const live = liveStateRef.current;
      if (live.type === 'include') live.ids.add(id);
      else live.ids.delete(id);
      if (instanceKey) writeExpansionEntry(instanceKey, live);
    },
    [apiRef, instanceKey],
  );

  const schedulePersist = React.useCallback(() => {
    if (isRestoringRef.current || !instanceKey) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      // Single grid scan per debounce window — coalesces bursts of toggles.
      const state = collectExpansionState(apiRef, modeRef.current);
      liveStateRef.current = state;
      writeExpansionEntry(instanceKey, state);
    }, PERSIST_DEBOUNCE_MS);
  }, [apiRef, instanceKey]);

  React.useEffect(() => {
    if (!instanceKey || !enabled || !apiRef.current) return;

    const shouldExpand = (id: number): boolean => {
      const { type, ids } = liveStateRef.current;
      return type === 'include' ? ids.has(id) : !ids.has(id);
    };

    const applyInitialExpansion = () => {
      if (!initialExpansionState || !apiRef.current) return;
      if (initialExpansionState.type === 'include') {
        for (const id of initialExpansionState.ids) setGroupExpansion(apiRef, id, true);
      } else {
        apiRef.current.expandAllRows();
        for (const id of initialExpansionState.ids) setGroupExpansion(apiRef, id, false);
      }
    };

    // Apply saved state once when rows first arrive; on later tree rebuilds,
    // re-apply expansion only to group nodes that are new since the last pass.
    // A parent recreated by a filter change re-adopts its intended expansion,
    // while individual toggles — which never introduce a new group id — are
    // left untouched.
    const initOrReconcile = () => {
      if (!apiRef.current) return;

      if (!hasInitializedRef.current) {
        if (apiRef.current.getAllRowIds().length === 0) return;
        isRestoringRef.current = true;
        applyInitialExpansion();
        isRestoringRef.current = false;
        appliedGroupIdsRef.current = collectGroupIds(apiRef);
        hasInitializedRef.current = true;
        return;
      }

      const groupIds = collectGroupIds(apiRef);
      const newIds = [...groupIds].filter((id) => !appliedGroupIdsRef.current.has(id));
      if (newIds.length > 0) {
        isRestoringRef.current = true;
        for (const id of newIds) setGroupExpansion(apiRef, id, shouldExpand(id));
        isRestoringRef.current = false;
      }
      appliedGroupIdsRef.current = groupIds;
    };

    const onExpansionChange = (node: GridGroupNode) => {
      if (isRestoringRef.current) return;
      // expand-all/collapse-all emit a single event on the root node; use it to
      // flip the storage mode. Individual toggles keep the current mode. The
      // expensive grid scan is deferred to the debounced persist.
      if (node.id === GRID_ROOT_GROUP_ID) {
        modeRef.current = node.childrenExpanded ? 'exclude' : 'include';
      }
      schedulePersist();
    };

    initOrReconcile();
    const unsubscribeRowsSet = apiRef.current.subscribeEvent('rowsSet', initOrReconcile);
    const unsubscribeExpansion = apiRef.current.subscribeEvent(
      'rowExpansionChange',
      onExpansionChange,
    );

    return () => {
      unsubscribeRowsSet();
      unsubscribeExpansion();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [apiRef, instanceKey, schedulePersist, initialExpansionState, enabled]);

  return { markExpanded };
};
